import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getUnitIdFromNodeId } from 'utils/AICreatorHelper';

import {
  buildMonitoringSessionTitle,
  filterMonitoringSessionCardsBySearch,
  filterMonitoringSessionsByType,
} from '../utils/monitoringSessionUtils';

import type { IAICardSelection } from 'components/AICardPanel/interfaces/IAICardSelection';
import type IMonitoringAgentDefinition from 'constants/interfaces/IMonitoringAgentDefinition';
import type ISessionVCItem from 'store/slices/valueChainMap/interfaces/ISessionVCItem';

const SESSION_DATE_FORMAT = 'MMM D';

interface IUseMonitoringSessionsParams {
  sessions: ISessionVCItem[],
  agentDefinition: IMonitoringAgentDefinition,
  allAvailableCards: IAICardSelection[],
  search: string,
}

const useMonitoringSessions = ({
  sessions,
  agentDefinition,
  allAvailableCards,
  search,
}: IUseMonitoringSessionsParams) => {
  const { t } = useTranslation();

  const availableCardByUnitId = useMemo(() => {
    const nextAvailableCardByUnitId = new Map<number, { id: number, name: string, type?: string }>();

    allAvailableCards.forEach((card) => {
      const unitId = getUnitIdFromNodeId(card.nodeId);

      if (unitId === null) {
        return;
      }

      nextAvailableCardByUnitId.set(unitId, {
        id: unitId,
        name: card.name,
        type: card.type,
      });
    });

    return nextAvailableCardByUnitId;
  }, [allAvailableCards]);

  const monitoringSessions = useMemo(
    () => filterMonitoringSessionsByType(sessions, agentDefinition.sessionType),
    [agentDefinition.sessionType, sessions],
  );

  const sessionCards = useMemo(() => (
    monitoringSessions.map((session) => ({
      session,
      title: buildMonitoringSessionTitle({
        session,
        agentDefinition,
        formatTitle: (key) => t(key),
        formatDate: (dateValue) => (
          dateValue && dayjs(dateValue).isValid()
            ? dayjs(dateValue).format(SESSION_DATE_FORMAT)
            : ''
        ),
      }),
      entities: (session.selectedUnits || []).map((selectedUnit) => {
        const knownCard = availableCardByUnitId.get(selectedUnit.unitId);

        if (knownCard) {
          return knownCard;
        }

        return {
          id: selectedUnit.unitId,
          name: selectedUnit.unit?.name
            || selectedUnit.name
            || selectedUnit.unitName
            || t('valueChainMap.aiSessionsFallbackEntityName', { id: selectedUnit.unitId }),
          type: selectedUnit.unit?.type || selectedUnit.type,
        };
      }),
    }))
  ), [agentDefinition, availableCardByUnitId, monitoringSessions, t]);

  const filteredSessionCards = useMemo(
    () => filterMonitoringSessionCardsBySearch(sessionCards, search),
    [search, sessionCards],
  );

  return {
    monitoringSessions,
    filteredSessionCards,
  };
};

export default useMonitoringSessions;
